import { Tour, TourImageDetail, TourPriceOption } from '@/types/tourBooking';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useRef } from 'react';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { Typography, Button, Grid, IconButton, Divider, Box, Container, CircularProgress, Card } from '@mui/material';
import {
  BookingContainer,
  BreadcrumbsBar,
  MainCard,
  InfoSection,
} from './style';
import { Trans, useTranslation } from 'react-i18next';
import NormalGallery from '@/components/NormalGallery';
import WebGallery from '@/components/WebGallery';
import useMediaQuery from '@mui/material/useMediaQuery';
import BookingFormPanel from './BookingFormPanel/BookingFormPanel';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import { useTheme } from '@mui/material/styles';
import { ITourBookingForm } from '@/types/interface';
import BookingConfirm, { PendingOrder } from './BookingConfirm';
import ConfirmModal from '@/components/ConfirmModal';
import PreLoading from '@/components/PreLoading';
import { handleLocalStorage } from '@/utils/handleLocalStorage';
import { convertMillisecondsToTimeOfDay } from '@/utils/formatTime';
import CustomErrorComponent from '@/pages/_error';
import { hoiangoShowLog } from '@/utils/log';
import useStoreApp from '@/store';
import { getMinPriceOfTour, queryToHomeUrl } from '@/utils/utils';
import useBrowserNavigationDetection from '@/hooks/useBrowserNavigationDetection';
import TourTimeline from '@/components/BookingFormCustom/TourTimeline';
import { SummarizationTourInfo, FloatingViewPackagesButton } from '@/components/BookingFormCustom';
import useEmbed, { PostMessageType } from '@/hooks/useEmbed';
import Price from '@/components/Price';

interface Props {
  tours?: Tour[];
  locale: string;
}

const TourBookingInfoDetail = ({ tours, locale }: Props) => {
  const { t } = useTranslation(['common', 'validate']);
  const router = useRouter();
  const theme = useTheme();
  const { sendPostMessage } = useEmbed();
  const { slug, from, language_id, scroll_to_booking } = router.query as { slug?: string, from?: string, language_id?: string, scroll_to_booking?: string };

  const { languages, setLanguageId, isEmbeed } = useStoreApp(state => state.globalSlice);

  const [mainImgIdx, setMainImgIdx] = useState(0);
  const [fade, setFade] = useState<'gallery-fade-in' | 'gallery-fade-out'>('gallery-fade-in');
  const [prevImgIdx, setPrevImgIdx] = useState<number | null>(null);
  const isMobile = useMediaQuery('(max-width:900px)');
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const [showFinishBookingNowButton, setShowFinishBookingNowButton] = useState(false);
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);
  const [isPriceCardVisible, setIsPriceCardVisible] = useState(true);
  const formSectionRef = React.useRef<HTMLDivElement>(null);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<PendingOrder | undefined>(undefined);

  const paymentInfoBoxRef = useRef<HTMLElement>(null);
  const isScrollingToPaymentRef = useRef(false);

  const contentRef = useRef<HTMLElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const [formSectionHeight, setFormSectionHeight] = useState<number | null>(null);
  const summarizationTourInfoRef = useRef<HTMLDivElement>(null);

  const [images, setImages] = useState<TourImageDetail[]>([{
    uid: '1',
    alt: '',
    url: '/assets/images/ic_app_logo.png',
  }]);

  const [isTourNotFound, setIsTourNotFound] = useState(false);

  const embedFromHoiAnGoCom = isEmbeed;

  const {
    tourDetail,
    isFetchingTourDetail,
    showConfirmModal,
    setShowConfirmModal,
    selectedTour,
    bookingTourData,
    computedPricing,
  } = useTourBookingContext();

  // Get the selected tour price option based on the selected duration
  const selectedTourPriceOption: TourPriceOption | null = React.useMemo(() => {
    if (!selectedTour?.tourPriceOptions || !bookingTourData?.tourPriceOptionId) return null;
    return selectedTour.tourPriceOptions.find(opt => opt.id === bookingTourData.tourPriceOptionId) || selectedTour.tourPriceOptions[0] || null;
  }, [selectedTour?.tourPriceOptions, bookingTourData?.tourPriceOptionId]);

  const { isBottomNavVisible } = useBrowserNavigationDetection();

  useEffect(() => {
    if (selectedTour) {
      if (selectedTour.imageDetails) {
        if (selectedTour.imageDetails.length === 1) {
          setImages(selectedTour.imageDetails);
        }
        else {
          const imagesWithoutFirst = selectedTour.imageDetails.slice(1, selectedTour.imageDetails.length);

          if (imagesWithoutFirst && imagesWithoutFirst.length > 0) {
            setImages(imagesWithoutFirst);
          }
        }
      }
      else if (selectedTour.images && selectedTour.images.length > 0) {
        setImages([]);
        for (let i = 0; i < selectedTour.images.length; i++) {
          const image = selectedTour.images[i];
          const imageDetail: TourImageDetail = {
            uid: i.toString(),
            alt: '',
            url: image,
          };
          setImages((prevImages) => [...prevImages, imageDetail]);
        }
      }

    }
  }, [selectedTour]);

  useEffect(() => {
    if (!isFetchingTourDetail && !tourDetail && !isTourNotFound) {
      setIsTourNotFound(true);
    }
    else if (isTourNotFound) {
      setIsTourNotFound(false);
    }
  }, [isFetchingTourDetail, tourDetail]);

  // Auto-resize postMessage for embed mode
  useEffect(() => {
    function sendHeight() {
      let height = document.documentElement.scrollHeight;

      if (showPaymentConfirmation) {
        height = document.getElementById('hoiango-detail-tour-booking-container-content')?.offsetHeight ?? 0;
      }
      sendPostMessage(PostMessageType.resize, height);
    }

    if (!tourDetail || isFetchingTourDetail) return;

    sendHeight();
    window.addEventListener('resize', sendHeight);

    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });

    resizeObserver.observe(document.body);

    return () => {
      window.removeEventListener('resize', sendHeight);
      resizeObserver.disconnect();
    };
  }, [tourDetail, isFetchingTourDetail, showPaymentConfirmation]);

  useEffect(() => {
    if (language_id) setLanguageId(language_id)
    else if (languages) {
      const lang = languages.find((e: any) => {
        if (router.locale === 'vi') return e.code.toLowerCase() === 'vn';
        else return e.code.toLowerCase() === 'en';
      });

      if (lang) setLanguageId(lang?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.locale, languages]);

  // IntersectionObserver to show/hide floating button
  useEffect(() => {
    if (isFetchingTourDetail || embedFromHoiAnGoCom) return;

    const ref = formSectionRef.current;

    if (!ref) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setShowFloatingButton(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1, // Adjust as needed
      }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [isFetchingTourDetail]);

  useEffect(() => {
    const ref = paymentInfoBoxRef.current;

    if (!ref) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (showPaymentConfirmation) {
          if (isScrollingToPaymentRef.current) {
            setShowFinishBookingNowButton(false);
          } else {
            setShowFinishBookingNowButton(!entry.isIntersecting);
          }
        }
      },
      {
        root: null,
        threshold: 0.1, // Adjust as needed
      }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [paymentInfoBoxRef.current, showPaymentConfirmation]);

  // IntersectionObserver to show/hide Price Card based on short content visibility
  useEffect(() => {
    if (isFetchingTourDetail || embedFromHoiAnGoCom) return;
    const ref = galleryRef.current;
    if (!ref) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        // Show Price Card when short content is visible, hide when it's not
        setIsPriceCardVisible(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1,
        rootMargin: '-500px 0px 0px 0px', // Account for sticky header offset
      }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [isFetchingTourDetail, embedFromHoiAnGoCom]);

  // ResizeObserver to track booking form height for right column matching
  useEffect(() => {
    if (isFetchingTourDetail) return;

    const ref = formSectionRef.current;
    if (!ref) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setFormSectionHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(ref);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isFetchingTourDetail, tourDetail]);

  useEffect(() => {

    const checkOrderConfirm = () => {
      const orderConfirmStorage = handleLocalStorage.get('order_confirm');

      if (orderConfirmStorage) {
        const orderConfirm = JSON.parse(orderConfirmStorage) as PendingOrder;

        if (orderConfirm.slug === slug && orderConfirm.payment_status === 'waiting') {

          setShowPaymentConfirmation(true)
          setPendingOrder(orderConfirm);
        }
        else {
          // data compare error
        }
      }
      else {
        // orderConfirm not found
      }
    }

    if (showPaymentConfirmation && pendingOrder) return;

    if (slug) {
      checkOrderConfirm();
    }

  }, [pendingOrder, showPaymentConfirmation, slug]);

  const _handleScrollToPaymentInfoBox = () => {
    if (paymentInfoBoxRef.current) {
      paymentInfoBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  const _handleScrollToFormSection = () => {
    if (formSectionRef.current) {
      const stickyOffset = 172; // header (72px) + sticky widget height (~80px) + some padding
      const elementPosition = formSectionRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - stickyOffset;

      if (embedFromHoiAnGoCom) {
        sendPostMessage(
          PostMessageType.scroll_to_form_section,
          {
            offset: offsetPosition
          }
        );
      }
      else {
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        setTimeout(() => {
          setShowFloatingButton(false)
        }, 500);
      }
    }
  }

  // TODO: If want to scroll to form section, remove this comment
  /*
  useEffect(() => {
    if (!isFetchingTourDetail && tourDetail && scroll_to_booking && (!embedFromHoiAnGoCom) && (isXs || isSm)) {
      setTimeout(() => {
        _handleScrollToFormSection();
      }, 1000);
    }
  }, [scroll_to_booking, embedFromHoiAnGoCom, isXs, isSm, isFetchingTourDetail, tourDetail]);
  */

  if (isFetchingTourDetail || (slug && !tourDetail)) {
    return <PreLoading />
  }
  else if (!slug ||
    (tours && !tours.find(t => t.slug === slug)) ||
    isTourNotFound) {
    return <CustomErrorComponent statusCode={404} sx={{
      background: theme.palette.blue[10],
      padding: 0,
    }} />
  }

  const handleThumbClick = (idx: number) => {
    if (idx === mainImgIdx) return;
    setPrevImgIdx(mainImgIdx);
    setFade('gallery-fade-out');
    setTimeout(() => {
      setMainImgIdx(idx);
      setFade('gallery-fade-in');
      setPrevImgIdx(null);
    }, 200); // match transition duration
  };

  const pushToHome = (e: any) => {
    e.stopPropagation();
    router.push({ pathname: queryToHomeUrl(router.locale ?? 'en', router) });
  };

  const handleBackButtonClick = (e: any) => {
    if (showPaymentConfirmation && !showConfirmModal && setShowConfirmModal) {
      setShowConfirmModal(prev => !prev);
      return;
    }
    pushToHome(e);
  }

  const handleShowConfirmModal = (formData: ITourBookingForm) => {
    setShowPaymentConfirmation(true);
  }

  // Handler to trigger form submission from floating SummarizationTourInfo
  const handleFloatingSubmitClick = () => {
    // Find the submit button inside the form and click it
    const formElement = formSectionRef.current;
    if (formElement) {
      const submitButton = formElement.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (submitButton) {
        submitButton.click();
      }
    }
  };

  const handleScrollToContent = () => {
    if (contentRef.current) {
      const stickyOffset = 80; // header (72px) + sticky widget height (~80px) + some padding
      const elementPosition = contentRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - stickyOffset;

      if (isEmbeed) {
        sendPostMessage(
          PostMessageType.scroll_to_highlight,
          {
            offset: offsetPosition
          }
        );
      }
      else {
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <BookingContainer id='hoiango-detail-tour-booking-container'>
      <Container maxWidth='xl' id='hoiango-detail-tour-booking-container-content'>
        {/* Breadcrumbs Bar */}
        {
          embedFromHoiAnGoCom ? <Box height={"12px"} /> : (
            <BreadcrumbsBar>
              <IconButton
                onClick={handleBackButtonClick}
                sx={{
                  xs: { mr: 1, ml: 0 },
                  md: { mr: 1, ml: 2 },
                  backgroundColor: 'transparent',
                  padding: 0,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Typography color="blue.steelBlue" variant="body1">{t('common:tourBooking')}</Typography>
                <Typography color="blue.steelBlue" sx={{ mx: theme.spacing(0.5), }} variant="body1">{'>'}</Typography>
                <Typography color="blue.main" flexWrap={'wrap'}>
                  {selectedTour?.tourContents?.[0]?.title || ''}
                </Typography>
              </IconButton>
            </BreadcrumbsBar>
          )
        }

        {/* Header Row: Title and Price Card - Desktop Only */}
        <Box sx={{
          display: showPaymentConfirmation ? 'none' : { xs: 'none', md: 'flex' },
          justifyContent: 'space-between',
          alignItems: 'center',
          my: theme.spacing(0),
          flexWrap: 'wrap',
          gap: theme.spacing(1),
          position: embedFromHoiAnGoCom ? 'relative' : 'sticky',
          top: embedFromHoiAnGoCom ? 'unset' : 72,
          zIndex: embedFromHoiAnGoCom ? 'unset' : 100,
          backgroundColor: theme.palette.blue.background,
          py: theme.spacing(1.5),
          px: theme.spacing(2),
          mx: theme.spacing(-2),
          transition: 'box-shadow 0.2s ease-in-out',
        }}>
          <Box>
            <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: 24, md: 32 }, color: 'black' }}>
              {selectedTour?.tourContents?.[0]?.title || ''}
            </Typography>
            {/*{tourDetail && tourDetail?.pickUpAddress ?
              (
                <Typography
                  variant="body1"
                  fontWeight={400}
                  color="black.jetBlack"
                  sx={{ mt: theme.spacing(1.2), display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}
                >
                  {t('validate:pick_up_point')}: <Typography
                    fontWeight={500}
                    color="blue.main"
                  >
                    {tourDetail?.pickUpAddress}
                  </Typography>
                </Typography>
              )
              : null
            }*/}
          </Box>
          {/* Price Card */}
          {selectedTour && (
            <Card sx={{
              p: 0.75,
              boxShadow: 'none',
              borderRadius: 2,
              minWidth: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 15,
              opacity: isPriceCardVisible ? 1 : 0,
              transform: isPriceCardVisible ? 'translateX(0)' : 'translateX(20px)',
              transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
              pointerEvents: isPriceCardVisible ? 'auto' : 'none',
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('from')}</Typography>
                <Typography variant="h3" fontWeight={700} color="blue.active">
                  <Price price={getMinPriceOfTour(selectedTour)} />
                  <Typography component="span" variant="body2">/{t('common:car')}</Typography>
                </Typography>
              </Box>
              <Button variant="contained" color="primary" sx={{ ml: 2, borderRadius: 2, whiteSpace: 'nowrap', boxShadow: 'none', padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`, height: '40px' }} onClick={_handleScrollToFormSection}>
                {t('validate:view_packages')}
              </Button>
            </Card>
          )}
        </Box>


        {/* Gallery */}
        <Box ref={galleryRef} sx={{ mb: 2, display: showPaymentConfirmation ? "none" : "block" }}>
          {isMobile ? (
            <NormalGallery
              images={images}
              mainImgIdx={mainImgIdx}
              prevImgIdx={prevImgIdx}
              fade={fade}
              onThumbClick={handleThumbClick}
              alt={selectedTour?.tourContents?.[0]?.title || ''}
            />
          ) : (
            <WebGallery
              images={images}
              alt={selectedTour?.tourContents?.[0]?.title || ''}
            />
          )}
        </Box>

        {/* Mobile Header Info (Mobile Only - Below Gallery) */}
        {!showPaymentConfirmation && (
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
            <Typography variant="h4" fontWeight={700} mb={1}>
              {selectedTour?.tourContents?.[0]?.title}
            </Typography>

            {/* Start Time */}
            {tourDetail?.startActiveTime && tourDetail?.endActiveTime && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {t('validate:tour_starts_every')} <span style={{ color: '#0066CC' }}>{convertMillisecondsToTimeOfDay(tourDetail.startActiveTime)}</span> {t('validate:to')} <span style={{ color: '#0066CC' }}>{convertMillisecondsToTimeOfDay(tourDetail.endActiveTime)}</span>
              </Typography>
            )}

            {/* Pickup */}
            {/*{tourDetail?.pickUpAddress && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('validate:pick_up_point')}: <span style={{ fontWeight: 500, color: '#0066CC' }}>{tourDetail.pickUpAddress}</span>
              </Typography>
            )}*/}

            {/* Hightlights Box */}
            {selectedTour?.tourContents?.[0]?.shortContent && (
              <Box sx={{ backgroundColor: '#fff', paddingBlock: 1, paddingInline: 1, borderRadius: 2, maxHeight: '164px !important', overflow: 'hidden' }}>
                {tourDetail && tourDetail?.tourContents ?
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{ __html: tourDetail?.tourContents?.[0]?.shortContent ?? "" }}
                    maxHeight={"80px"}
                    overflow={'hidden'}
                    sx={{
                      color: '#000000',
                      "& p": { margin: "0 !important", lineHeight: "1.3 !important" }
                    }}
                  />
                  : null
                }
                <HightLightLabel onClick={handleScrollToContent} text={t('common:highLightShortContent')} />
              </Box>
            )}

          </Box>
        )}

        {/* Booking Confirm */}
        <>
          <BookingConfirm
            pendingOrder={pendingOrder}
            sx={{
              display: showPaymentConfirmation ? null : "none"
            }}
            paymentInfoBoxRef={paymentInfoBoxRef}
          />
          {/*  Floating Confirm Now Button for xs only - Legacy (kept inside this block but visibility controlled) */}
          {!embedFromHoiAnGoCom && showPaymentConfirmation && (
            <Box
              className={`fade-slide-up ${showFinishBookingNowButton ? 'fade-slide-up--visible' : 'fade-slide-up--hidden'}`}
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1301,
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                pointerEvents: showFinishBookingNowButton ? 'auto' : 'none',
                paddingBottom: isBottomNavVisible ? '8px' : '0px',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{
                  m: 2,
                  borderRadius: 8,
                  width: '90%',
                  fontWeight: 700,
                  fontSize: 18,
                  boxShadow: 3,
                  pointerEvents: 'auto',
                }}
                onClick={_handleScrollToPaymentInfoBox}
              >
                {t('finishBookingNow')}
              </Button>
            </Box>
          )}
          {/* Floating View Packages Button for mobile - shows when form is scrolled out of view */}
          {!embedFromHoiAnGoCom && !showPaymentConfirmation && (
            <FloatingViewPackagesButton
              isVisible={showFloatingButton}
              isBottomNavVisible={isBottomNavVisible}
              onScrollToFormSection={_handleScrollToFormSection}
            />
          )}
        </>


        {/* Main Content Grid: Booking Form Left, Timeline Right */}
        <MainCard sx={{ display: showPaymentConfirmation ? "none" : null, padding: 0 }}>
          {/* Tour Highlights - Full Width Above Grid */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  mb: 3,
                  display: { xs: 'none', md: 'block' },
                  backgroundColor: '#fff',
                  p: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  maxHeight: '164px',
                }}>
                {tourDetail && tourDetail?.tourContents ?
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{ __html: tourDetail?.tourContents?.[0]?.shortContent ?? "" }}
                    maxHeight={"80px"}
                    overflow={'hidden'}
                    sx={{
                      color: '#000000',
                      "& p": { margin: "0 !important", lineHeight: "1.3 !important" }
                    }}
                  />
                  : null
                }
                <HightLightLabel onClick={handleScrollToContent} text={t('common:highLightShortContent')} />
              </Box>
            </Grid>

          </Grid>

          <Grid container spacing={4}>
            {/* Left Column: Booking Form */}
            <Grid item xs={12} md={8}>
              <Box id='hoiango-detail-tour-booking-form-panel' ref={formSectionRef} sx={{ mb: 4 }}>
                <Typography variant="h2" fontWeight={700} mb={"10px"}>{t('validate:service_packages_title')}</Typography>
                <BookingFormPanel
                  onSubmitBooking={handleShowConfirmModal}
                  onSummaryVisibilityChange={setIsSummaryVisible}
                />
              </Box>
              {/* Description Content - Full Width Below Grid */}
              <Box sx={{ my: 4 }} ref={contentRef}>
                {tourDetail && tourDetail?.tourContents ?
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    mb={2}
                    dangerouslySetInnerHTML={{ __html: tourDetail?.tourContents?.[0]?.content ?? "" }}
                    sx={{
                      color: '#000000',
                      '& figure.table': {
                        width: '100%',
                        overflowX: 'auto',
                        marginInline: '0px',
                        /*
                        border: `solid 1px ${theme.palette.other.divider}`,
                        borderRadius: '4px',
                        'table': {
                          borderSpacing: '0px !important',
                          width: '100%',
                        },
                        "thead": {
                          backgroundColor: theme.palette.other.divider,
                          'tr': {
                            'th': {
                              textAlign: 'start',
                              paddingInline: '8px',
                              paddingBlock: '4px',
                              borderBottom: `solid 1px ${theme.palette.other.divider}`,
                            },
                          },
                        },
                        "tbody": {
                          "tr": {
                            'td': {
                              paddingInline: '8px',
                              paddingBlock: '4px',
                              borderBottom: `solid 1px ${theme.palette.other.divider}`,
                            },
                          },
                          'tr:last-child': {
                            'td': {
                              borderBottom: 'unset',
                            },
                          },

                        }
                        */
                      },
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 8
                      },
                      "& p": {
                        margin: "0 !important",
                        lineHeight: "1.3 !important"
                      }
                    }}
                  />
                  : null
                }
              </Box>
            </Grid>

            {/* Right Column: Timeline */}
            <Grid item xs={12} md={4} display={{ xs: 'none', md: 'block' }} marginTop={4}>
              <Box
                sx={{
                  height: formSectionHeight ? `${formSectionHeight}px` : 'auto',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.grey[300],
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: theme.palette.grey[400],
                  },
                  borderRadius: 2, border: `1px solid ${theme.palette.white[70]}`
                }}
              >
                <TourTimeline selectedTourPriceOption={selectedTourPriceOption} isBottomSheet={false} />
              </Box>
            </Grid>
          </Grid>

          {/* Floating SummarizationTourInfo for mobile - Fixed at bottom */}
          {
            (!embedFromHoiAnGoCom) && (isXs || isSm) && selectedTour ? (
              <Box
                ref={summarizationTourInfoRef}
                sx={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1000,
                  display: { xs: 'block', md: 'none' },
                  transform: showFloatingButton ? 'translateY(100%)' : 'translateY(0)',
                  opacity: showFloatingButton ? 0 : 1,
                  transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                  pointerEvents: showFloatingButton ? 'none' : 'auto',
                }}
              >
                <SummarizationTourInfo
                  computedPricing={computedPricing}
                  isDisableSubmit={false}
                  onSubmitClick={handleFloatingSubmitClick}
                  sxContainer={{
                    boxShadow: '0px -4px 10px rgba(0,0,0,0.1)',
                    borderRadius: '16px 16px 0 0',
                    backgroundColor: 'primary.contrastText',
                    ml: '0',
                    width: '100%',
                    padding: '0.75rem',
                  }}
                  sxLeft={{
                    alignItems: {
                      xs: 'flex-start',
                      md: 'flex-start',
                    },
                  }}
                  toolTipPosition="behindTotal"
                  summaryType='customer'
                />
              </Box>
            ) : null
          }
        </MainCard>

        <ConfirmModal
          isOpen={showConfirmModal ?? false}
          title={t('validate:modal.bookingTour.askToLeaveTitle')}
          description={t('validate:modal.bookingTour.askToLeaveDescription') ?? ''}
          yesAction={() => {
            setShowPaymentConfirmation(false)
            setShowConfirmModal?.(false)
            handleLocalStorage.remove('order_confirm')
          }}
          noAction={() => setShowConfirmModal?.(false)}
        />
      </Container>
    </BookingContainer>
  );
};

const HightLightLabel = ({ onClick, text }: { onClick: () => void, text: string }) => {
  return (
    <Typography
      variant="subtitle2"
      color="blue.highlight"
      onClick={onClick}
      sx={{
        mt: 0.5,
        width: 'fit-content',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      {text}
    </Typography>
  );
};

export default TourBookingInfoDetail;
